import { ThemeVariables } from '@/themes/mpanels/variables';
import { addOpacityToThemeColor } from '@/utils/styles/addOpacity';
import DOMPurify from 'isomorphic-dompurify';

export type StyleObject = {
    [key: string]: {
        [property: string]: string | number;
    };
};

export const convertStyleObjectToString = (styleObject: StyleObject): string =>
    Object.entries(styleObject)
        .map(
            ([selector, styles]) =>
                `${selector}{${Object.entries(styles)
                    .map(
                        ([property, value]) =>
                            // Convert camelCase to kebab-case for CSS properties
                            `${property.replace(/[A-Z]/g, (l) => `-${l.toLowerCase()}`)}:${value}`,
                    )
                    .join(';')}}`,
        )
        .join('');

export const generateGlobalStyles = <T extends boolean | undefined>({
    variables,
    asString,
}: {
    variables: ThemeVariables;
    asString?: T;
}): T extends true ? string : StyleObject => {
    const {
        themeName,
        colors,
        fonts,
        padding,
        margin,
        borderRadius,
        boxShadows,
        cookie,
        typography,
    } = variables;

    const globalStyles: StyleObject = {
        ':root': {
            '--color-primary': colors.palette.primary.main,
            '--color-primary-contrast': colors.palette.primary.contrastText,
            '--color-error': colors.other.text.error,
            '--color-warning': colors.other.text.warning,
            '--color-secondary': colors.palette.secondary.main,
            '--color-background': colors.palette.background.default,
            '--color-text-primary': colors.palette.text.primary,
            '--color-background-header': colors.other.background.header,
            '--color-background-topmenu': colors.other.background.topMenu,
            '--color-text-registration-description': colors.other.text.registrationDescription,
            '--color-text-header': colors.other.text.header,
            '--header-shadow': boxShadows.headerShadow,
            '--color-background-footer': colors.other.background.footer,
            '--color-text-footer': colors.other.text.footer,
            '--color-text-copyright-text': colors.other.text.copyrightText,
            '--color-background-highlight': colors.other.background.highlight,
            '--color-hover-highlight': colors.other.background.highlightHover,
            '--color-light-gray': colors.other.text.lightGray,
            '--color-dark-gray': colors.other.text.darkGray,
            '--color-divideline': colors.other.background.divideline,
            '--color-link': colors.links.main,
            '--color-link-hover': colors.links.hover,
            '--color-link-active': colors.links.active,
            '--color-link-disabled': colors.links.disabled,
            '--font-primary': fonts.primary,
            '--font-secondary': fonts.secondary,
            '--font-additional': fonts?.additional ?? fonts.secondary,
            '--font-body-font-size': fonts?.body?.fontSize ?? '',
            '--border-radius-primary': borderRadius.primary,
            '--border-radius-secondary': borderRadius.secondary,
            '--border-radius-tertiary': borderRadius.tertiary,
            '--color-evenrow-background': colors.other.table.evenRowHighlight,
            '--color-light-table-border': addOpacityToThemeColor(colors.palette.text.primary, 0.2),
            '--table-header-text-color': colors.other.table.headerText,
            '--table-text-color': colors.other.table.text,
            '--table-text-hover-color': colors.other.table.hoverText,
            '--table-hover-background-color': colors.other.table.hoverHighlight,
            '--table-evenrow-highlight': colors.other.table.evenRowHighlight,
            '--table-cellpadding': padding.tableCellPadding,
            '--table-shadow': boxShadows.tableShadow,
            '--font-family-h1': typography?.h1?.fontFamily ?? '',
            '--font-weight-h1': typography?.h1?.fontWeight ?? '',
            '--font-size-h1': typography?.h1?.fontSize ?? '',
            '--font-family-h2': typography?.h2?.fontFamily ?? '',
            '--font-weight-h2': typography?.h2?.fontWeight ?? '',
            '--font-size-h2': typography?.h2?.fontSize ?? '',
            '--font-family-h3': typography?.h3?.fontFamily ?? '',
            '--font-weight-h3': typography?.h3?.fontWeight ?? '',
            '--font-size-h3': typography?.h3?.fontSize ?? '',
            '--font-family-h4': typography?.h4?.fontFamily ?? '',
            '--font-weight-h4': typography?.h4?.fontWeight ?? '',
            '--font-size-h4': typography?.h4?.fontSize ?? '',
            '--font-family-h5': typography?.h5?.fontFamily ?? '',
            '--font-weight-h5': typography?.h5?.fontWeight ?? '',
            '--font-size-h5': typography?.h5?.fontSize ?? '',
            '--font-family-h6': typography?.h6?.fontFamily ?? '',
            '--font-weight-h6': typography?.h6?.fontWeight ?? '',
            '--font-size-h6': typography?.h6?.fontSize ?? '',
            '--font-family-paragraph': typography?.body1?.fontFamily ?? '',
            '--font-size-paragraph': typography?.body1?.fontSize ?? '',
            '--link-text-decoration': typography?.link?.textDecoration ?? '',
            '--link-hover-text-decoration': typography?.link?.['&:hover']?.textDecoration ?? '',
            '--link-active-text-decoration': typography?.link?.['&:active']?.textDecoration ?? '',
            '--link-disabled-text-decoration':
                typography?.link?.['&:disabled']?.textDecoration ?? '',
            '--default-element-padding': padding.elementPadding,
            '--default-element-margin': margin.elementMargin,
            '--hr-margin': margin.hrMargin,
            '--cookie-button-shadow': cookie.button.shadow,
            '--cookie-button-hover-background': cookie.button.hover.backgroundColor,
            '--cookie-button-hover-color': cookie.button.hover.color,
            '--cookie-button-border-radius': cookie.button.borderRadius,
            '--cookie-toggle-initial-selected-toggleColor':
                cookie.toggle.initial.selected.toggleColor,
            '--cookie-toggle-initial-selected-backgroundColor':
                cookie.toggle.initial.selected.backgroundColor,
            '--cookie-toggle-initial-unselected-toggleColor':
                cookie.toggle.initial.unselected.toggleColor,
            '--cookie-toggle-initial-unselected-backgroundColor':
                cookie.toggle.initial.unselected.backgroundColor,
            '--cookie-toggle-disabled-toggleColor': cookie.toggle.disabled.toggleColor,
            '--cookie-toggle-disabled-backgroundColor': cookie.toggle.disabled.backgroundColor,
        },
    };

    if (themeName === 'm3gr') {
        globalStyles['@font-face'] = {
            fontFamily: 'Aptos',
            src: `url('/fonts/Aptos-ExtraBold.woff2') format('woff2'), url('/fonts/Aptos-ExtraBold.woff') format('woff')`,
            fontWeight: '800',
            fontStyle: 'normal',
        };
    }

    if (asString) {
        const stylesString = convertStyleObjectToString(globalStyles);
        return DOMPurify.sanitize(stylesString) as T extends true ? string : StyleObject;
    }

    return globalStyles as unknown as T extends true ? string : StyleObject;
};
